/* Projected Signage Calculator
   Original calculator logic by Cameron Melville (GPLv3).
   Adapted into a Shopify section custom element with customiser-driven
   unit/preset/email/text configuration. */

class ProjectedSignageCalculator extends HTMLElement {
  connectedCallback() {
    const dataEl = this.querySelector('[data-calc-config]');
    if (!dataEl) return;

    try {
      this.config = JSON.parse(dataEl.textContent);
    } catch (err) {
      console.error('Projected signage calculator: invalid config JSON', err);
      return;
    }

    this.text = this.config.text || {};
    this.email = this.config.email || {};
    this.units = Array.isArray(this.config.units) ? this.config.units : [];
    this.presets = this.config.presets || {};

    this.isMetric = (this.config.defaults && this.config.defaults.units) === 'metric';

    this.els = {
      presetSelect: this.querySelector('[data-calc-preset]'),
      envSelect: this.querySelector('[data-calc-env]'),
      unitSelect: this.querySelector('[data-calc-unit-system]'),
      luxInput: this.querySelector('[data-calc-lux]'),
      goboSelect: this.querySelector('[data-calc-gobo]'),
      surfaceSelect: this.querySelector('[data-calc-surface]'),
      modeSelect: this.querySelector('[data-calc-mode]'),
      distContainer: this.querySelector('[data-calc-dist-group]'),
      sizeContainer: this.querySelector('[data-calc-size-group]'),
      distSlider: this.querySelector('[data-calc-dist-slider]'),
      distInput: this.querySelector('[data-calc-dist-input]'),
      sizeSlider: this.querySelector('[data-calc-size-slider]'),
      sizeInput: this.querySelector('[data-calc-size-input]'),
      distLabel: this.querySelector('[data-calc-dist-label]'),
      sizeLabel: this.querySelector('[data-calc-size-label]'),
      illumUnits: this.querySelectorAll('[data-calc-illum-unit]'),
      banner: this.querySelector('[data-calc-banner]'),
      grid: this.querySelector('[data-calc-grid]'),
    };

    this.buildGrid();
    this.attachListeners();
    this.calculate();
  }

  buildGrid() {
    const { grid } = this.els;
    if (!grid) return;
    grid.innerHTML = '';

    this.units.forEach((unit) => {
      const card = document.createElement('div');
      card.className = 'projected-calc__unit';
      card.dataset.unitId = unit.id;

      const lenses = unit.lenses
        .map(
          (lens, i) => `
            <div class="projected-calc__lens" data-lens-index="${i}">
              <div class="projected-calc__lens-header">
                <span>${lens.angle}° ${this.t('lens_label', 'Lens')}</span>
                <span class="projected-calc__badge projected-calc__badge--ok" data-lens-badge>${this.t('in_spec', 'In Spec')}</span>
              </div>
              <div class="projected-calc__metrics" data-lens-metrics>
                <div class="projected-calc__metric">
                  <span class="projected-calc__metric-label" data-lens-output-label>${this.t('image_size', 'Image Size')}</span>
                  <span class="projected-calc__metric-value" data-lens-size>0.00</span>
                </div>
                <div class="projected-calc__metric">
                  <span class="projected-calc__metric-label">${this.t('req_lumens', 'Req. Lumens')}</span>
                  <span class="projected-calc__metric-value" data-lens-lumens>0 lm</span>
                </div>
              </div>
              <div class="projected-calc__error" data-lens-error hidden></div>
            </div>
          `
        )
        .join('');

      card.innerHTML = `
        <div class="projected-calc__unit-header">
          <h3>${this.escape(unit.name)}</h3>
        </div>
        <div class="projected-calc__lenses">${lenses}</div>
      `;

      grid.appendChild(card);
    });
  }

  attachListeners() {
    const { presetSelect, envSelect, unitSelect, luxInput, goboSelect, surfaceSelect,
      modeSelect, distSlider, distInput, sizeSlider, sizeInput } = this.els;

    const syncAndRecalc = (mirror) => {
      if (mirror) mirror();
      if (presetSelect) presetSelect.value = 'custom';
      this.calculate();
    };

    distSlider?.addEventListener('input', () => syncAndRecalc(() => { distInput.value = distSlider.value; }));
    distInput?.addEventListener('input', () => syncAndRecalc(() => { distSlider.value = distInput.value; }));
    sizeSlider?.addEventListener('input', () => syncAndRecalc(() => { sizeInput.value = sizeSlider.value; }));
    sizeInput?.addEventListener('input', () => syncAndRecalc(() => { sizeSlider.value = sizeInput.value; }));

    luxInput?.addEventListener('input', () => syncAndRecalc());
    goboSelect?.addEventListener('change', () => syncAndRecalc());
    surfaceSelect?.addEventListener('change', () => syncAndRecalc());
    envSelect?.addEventListener('change', () => syncAndRecalc());

    modeSelect?.addEventListener('change', () => {
      this.toggleMode();
      syncAndRecalc();
    });

    unitSelect?.addEventListener('change', (e) => this.switchUnitSystem(e.target.value === 'metric'));
    presetSelect?.addEventListener('change', (e) => this.applyPreset(e.target.value));
  }

  toggleMode() {
    const { modeSelect, distContainer, sizeContainer } = this.els;
    const isDistance = modeSelect.value === 'distance';
    if (distContainer) distContainer.hidden = !isDistance;
    if (sizeContainer) sizeContainer.hidden = isDistance;
  }

  switchUnitSystem(toMetric) {
    if (toMetric === this.isMetric) return;
    const { distSlider, distInput, sizeSlider, sizeInput, luxInput, illumUnits } = this.els;

    let dist = parseFloat(distInput.value);
    let size = parseFloat(sizeInput.value);

    if (toMetric) {
      dist = dist / 3.28084;
      size = size / 3.28084;
      distSlider.max = 40; distSlider.step = 0.5;
      sizeSlider.max = 20; sizeSlider.step = 0.1;
    } else {
      dist = dist * 3.28084;
      size = size * 3.28084;
      distSlider.max = 130; distSlider.step = 1;
      sizeSlider.max = 65; sizeSlider.step = 0.5;
    }

    distSlider.value = dist.toFixed(1);
    distInput.value = dist.toFixed(1);
    sizeSlider.value = size.toFixed(1);
    sizeInput.value = size.toFixed(1);
    this.isMetric = toMetric;

    illumUnits.forEach((el) => {
      el.textContent = this.isMetric ? this.t('unit_lux', 'Lux') : this.t('unit_fc', 'FC');
    });

    const currentIllum = parseFloat(luxInput.value);
    if (!isNaN(currentIllum)) {
      luxInput.value = Math.round(toMetric ? currentIllum * 10.7639 : currentIllum / 10.7639);
    }
    this.calculate();
  }

  applyPreset(key) {
    if (key === 'custom') return;
    const p = this.presets[key];
    if (!p) return;

    const { distSlider, distInput, luxInput, envSelect, goboSelect, surfaceSelect, modeSelect,
      distContainer, sizeContainer } = this.els;

    const dist = this.isMetric ? p.distMeters : p.distMeters * 3.28084;
    distInput.value = dist.toFixed(1);
    distSlider.value = dist.toFixed(1);

    const illum = this.isMetric ? p.lux : p.lux / 10.7639;
    luxInput.value = Math.round(illum);

    envSelect.value = p.env || 'indoor';
    goboSelect.value = '1.0';
    surfaceSelect.value = '1.0';

    modeSelect.value = 'distance';
    if (distContainer) distContainer.hidden = false;
    if (sizeContainer) sizeContainer.hidden = true;

    this.calculate();
  }

  calculate() {
    const { modeSelect, distInput, sizeInput, luxInput, envSelect, goboSelect, surfaceSelect,
      distLabel, sizeLabel, banner, grid } = this.els;

    const mode = modeSelect.value;
    const d = Math.max(0.01, parseFloat(distInput.value) || 1);
    const s = Math.max(0.01, parseFloat(sizeInput.value) || 1);
    const targetLux = parseFloat(luxInput.value) || 300;
    const isOutdoor = envSelect.value === 'outdoor';
    const colorLoss = parseFloat(goboSelect.value) || 1.0;
    const surfaceMult = parseFloat(surfaceSelect.value) || 1.0;

    const dMeters = this.isMetric ? d : d / 3.28084;
    const sMeters = this.isMetric ? s : s / 3.28084;
    const luxValue = this.isMetric ? targetLux : targetLux * 10.7639;

    const distUnit = this.isMetric ? 'm' : 'ft';
    distLabel.textContent = `${d.toFixed(1)} ${distUnit}`;
    sizeLabel.textContent = `${s.toFixed(1)} ${distUnit}`;

    const viableUnits = [];

    grid.querySelectorAll('.projected-calc__unit').forEach((c) => c.classList.remove('is-recommended'));

    this.units.forEach((unit) => {
      const card = grid.querySelector(`[data-unit-id="${unit.id}"]`);
      if (!card) return;

      let allOut = true;
      let anyInSpec = false;
      const envFail = isOutdoor && !unit.outdoor;

      unit.lenses.forEach((lens, index) => {
        let reqDistMeters = 0;
        let projSizeMeters = 0;

        if (mode === 'distance') {
          reqDistMeters = dMeters;
          projSizeMeters = dMeters * lens.ratio;
        } else {
          projSizeMeters = sMeters;
          reqDistMeters = sMeters / lens.ratio;
        }

        const trueAngleRad = lens.angle * (Math.PI / 180);
        const solidAngle = 2 * Math.PI * (1 - Math.cos(trueAngleRad / 2));
        const baseLumens = luxValue * (solidAngle * Math.pow(reqDistMeters, 2));
        const finalLumens = baseLumens * colorLoss * surfaceMult;

        const maxDist = lens.max === null || lens.max === undefined ? Infinity : lens.max;
        const distShortFail = reqDistMeters < lens.min;
        const distLongFail = reqDistMeters >= maxDist;
        const lumenFail = finalLumens > lens.maxLumens * 0.85;

        const isOut = envFail || distShortFail || distLongFail || lumenFail;
        if (!isOut) { allOut = false; anyInSpec = true; }

        const row = card.querySelectorAll('.projected-calc__lens')[index];
        const badge = row.querySelector('[data-lens-badge]');
        const metrics = row.querySelector('[data-lens-metrics]');
        const errEl = row.querySelector('[data-lens-error]');
        const sizeOut = row.querySelector('[data-lens-size]');
        const lumensOut = row.querySelector('[data-lens-lumens]');
        const outputLabel = row.querySelector('[data-lens-output-label]');

        if (mode === 'distance') {
          outputLabel.textContent = this.t('image_size', 'Image Size');
          const display = this.isMetric ? projSizeMeters : projSizeMeters * 3.28084;
          sizeOut.textContent = `${display.toFixed(2)} ${distUnit}`;
        } else {
          outputLabel.textContent = this.t('req_distance', 'Req. Distance');
          const display = this.isMetric ? reqDistMeters : reqDistMeters * 3.28084;
          sizeOut.textContent = `${display.toFixed(2)} ${distUnit}`;
        }

        if (isOut) {
          row.classList.add('is-out');
          badge.className = 'projected-calc__badge projected-calc__badge--error';
          badge.textContent = this.t('out_of_spec', 'Out of Spec');
          metrics.hidden = true;
          errEl.hidden = false;

          if (envFail) errEl.textContent = this.t('err_outdoor', '⚠️ Not suitable for outdoor use');
          else if (distShortFail) errEl.textContent = this.t('err_too_short', '⚠️ Distance too short to focus');
          else if (lumenFail) errEl.textContent = this.t('err_too_bright', '⚠️ Exceeds unit brightness limit');
          else errEl.textContent = this.t('err_too_long', '⚠️ Projection dulls at this distance');
        } else {
          row.classList.remove('is-out');
          badge.className = 'projected-calc__badge projected-calc__badge--ok';
          badge.textContent = this.t('in_spec', 'In Spec');
          metrics.hidden = false;
          errEl.hidden = true;
          lumensOut.textContent = `${Math.round(finalLumens).toLocaleString()} lm`;
        }
      });

      if (anyInSpec) viableUnits.push(unit);
      card.classList.toggle('is-all-out', allOut);
    });

    this.renderRecommendation(viableUnits, mode);
  }

  renderRecommendation(viableUnits, mode) {
    const { banner, grid, envSelect, goboSelect, surfaceSelect, luxInput, distLabel, sizeLabel } = this.els;

    if (!viableUnits.length) {
      banner.className = 'projected-calc__banner is-error';
      banner.innerHTML = this.t('no_units_message', '⚠️ <strong>No units are in spec</strong> for this setup. Please adjust your requirements.');
      return;
    }

    viableUnits.sort((a, b) => a.price - b.price);
    const best = viableUnits[0];

    const envText = envSelect.options[envSelect.selectedIndex].text;
    const goboText = goboSelect.options[goboSelect.selectedIndex].text;
    const surfaceText = surfaceSelect.options[surfaceSelect.selectedIndex].text;
    const targetLuxText = `${luxInput.value} ${this.isMetric ? this.t('unit_lux', 'Lux') : this.t('unit_fc', 'FC')}`;

    const scaleReq = mode === 'distance'
      ? `- ${this.t('throw_distance', 'Throw Distance')}: ${distLabel.textContent}`
      : `- ${this.t('image_size', 'Target Image Size')}: ${sizeLabel.textContent}`;

    const tokens = {
      unit_name: best.name,
      environment: envText,
      surface: surfaceText,
      scale_requirement: scaleReq,
      target_illuminance: targetLuxText,
      gobo_colors: goboText,
    };

    const subject = this.interpolate(this.email.subject || 'Quote Request: {unit_name}', tokens);
    const body = this.interpolate(this.email.body || '', tokens);
    const mailto = `mailto:${this.email.sales_email || ''}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;

    const prefix = this.t('recommended_prefix', '<strong>Recommended Unit:</strong>');
    const cta = this.escape(this.t('quote_button', 'Contact us for a quote'));

    banner.className = 'projected-calc__banner is-active';
    banner.innerHTML = `
      <div class="projected-calc__banner-content">
        <span>${prefix} ${this.escape(best.name)}</span>
        <a href="${mailto}" class="button button--primary projected-calc__quote-btn">${cta}</a>
      </div>
    `;

    const card = grid.querySelector(`[data-unit-id="${best.id}"]`);
    if (card) card.classList.add('is-recommended');
  }

  interpolate(template, tokens) {
    return String(template).replace(/\{(\w+)\}/g, (m, key) => (key in tokens ? tokens[key] : m));
  }

  t(key, fallback) {
    return this.text[key] || fallback;
  }

  escape(str) {
    return String(str ?? '').replace(/[&<>"']/g, (c) => ({
      '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
    }[c]));
  }
}

customElements.define('projected-signage-calculator', ProjectedSignageCalculator);
